import { useIsFeatureRolloutEnabled } from '@ms/yammer-experiments-context';
import { acquireToken } from '@ms/yammer-web-authenticators';
import type { Context, FC, PropsWithChildren } from 'react';
import React, { createContext, useContext, useMemo } from 'react';

import type { RelayContextType } from '../types';

export type LazyRelayAppWrapperProps = Omit<RelayContextType, 'environment'>;

/* istanbul ignore next */
export const LazyRelayAppWrapper: FC<PropsWithChildren<LazyRelayAppWrapperProps>> = ({
  children,
  ...relayContextWithoutEnvironment
}) => {
  const isAutomaticPersistedQueriesEnabled = useIsFeatureRolloutEnabled('AutomaticPersistedQueries');
  const isTracingEnabled = useIsFeatureRolloutEnabled('GraphqlForcedTracing');
  const isIncrementalDeliveryEnabled = useIsFeatureRolloutEnabled('IncrementalDelivery');

  const environment = useMemo(
    async () =>
      (await import(/* webpackChunkName: "4-feat-relay" */ '../environment')).createRelayEnvironment({
        ...relayContextWithoutEnvironment,
        acquireToken: () => acquireToken({ scopes: [relayContextWithoutEnvironment.authScope] }),
        isAutomaticPersistedQueriesEnabled,
        isTracingEnabled,
        isIncrementalDeliveryEnabled,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [relayContextWithoutEnvironment]
  );

  const relayContext = useMemo(
    () => ({
      ...relayContextWithoutEnvironment,
      environment,
    }),
    [environment, relayContextWithoutEnvironment]
  );

  return <RelayContext.Provider value={relayContext}>{children}</RelayContext.Provider>;
};

/* istanbul ignore next */
export const RelayContext: Context<RelayContextType> = createContext<RelayContextType>({
  environment: undefined,
  host: '',
  authScope: '',
});

/* istanbul ignore next */
export const useRelayContext = (): RelayContextType => useContext(RelayContext);
